/**
 * Templatify integration step — two explicit modes, no auto-create toggle:
 *
 *  «Create new account»      name · group (all Templatify groups) · api-key
 *                            · global-templates toggle · applications
 *                            (multi-select over all Templatify apps)
 *  «Link to existing account» searchable account picker · api-key (with the
 *                            confirm-guarded update toggle) · a Validate
 *                            button that fetches the live Templatify card
 *                            (name, group, applications, global access).
 */

import React, { useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckCircleOutline as ValidateIcon } from "@mui/icons-material";
import { useNotify, useTranslate } from "react-admin";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import type { AccountFormValues } from "../../types/account";
import { useAccountLookup } from "../../hooks/useAccountLookup";
import {
  getTemplatifyAccountDetails,
  validateTemplatifyCredentials,
  type TemplatifyAccountDetails,
  type TemplatifyTemplateOption,
} from "../../services/accountService";
import ApiKeyField from "../fields/ApiKeyField";
import UpdateTemplatifyKeyConfirmDialog from "../dialogs/UpdateTemplatifyKeyConfirmDialog";
import { generateApiKey } from "../../constants";

const i18n = "resources.accounts";

export const TemplatifyStep: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const rg = theme.custom.rg;

  const modeCardSx = (active: boolean) => ({
    p: "12px 14px",
    cursor: "pointer",
    border: `1.5px solid ${active ? rg.accent : rg.border}`,
    borderRadius: "12px",
    backgroundColor: active ? rg.accentWeak : rg.bgElev,
    transition: "all .15s",
  });

  const microSx = {
    fontSize: "10.5px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    fontWeight: 600,
    color: rg.text3,
  };

  // NOTE: no "& > *:first-of-type { flex: 1 }" here — that selector matches
  // the first child of EACH element type (the text div AND the switch span),
  // which stretched every toggle to full width. The text block carries its
  // own flex: 1 instead.
  const bannerSx = {
    p: "12px 14px",
    borderRadius: "10px",
    backgroundColor: rg.bg2,
    display: "flex",
    alignItems: "center",
    gap: 2,
  };

  const { control, setValue } = useFormContext<AccountFormValues>();
  const accountMode = useWatch({ control, name: "accountMode" });
  const autoCreateGlobal = useWatch({ control, name: "autoCreateGlobal" });
  const isUpdateTemplateApiKey = useWatch({
    control,
    name: "isUpdateTemplateApiKey",
  });
  const rmkExistTemplateAccount = useWatch({
    control,
    name: "rmkExistTemplateAccount",
  });
  const templatifyGroupId = useWatch({ control, name: "templatifyGroupId" });
  const templatifyExtraApplicationIds =
    useWatch({ control, name: "templatifyExtraApplicationIds" }) ?? [];

  const { data: existingAccounts = [] } =
    useAccountLookup("templatifyAccounts");
  const { data: groups = [] } = useAccountLookup("templatifyGroups");
  const { data: templatifyApps = [] } = useAccountLookup("templatifyApps");
  const applications = useWatch({ control, name: "applications" }) ?? [];

  // Only Templatify accounts that contain EVERY application linked to this
  // Notify account are linkable (matched by name, case-insensitive). The
  // currently selected account stays visible even if it no longer matches.
  const notifyAppNames = applications
    .filter((a) => a.applicationId && a.name)
    .map((a) => a.name.trim().toLowerCase());
  const linkableAccounts = existingAccounts.filter((acc) => {
    if (acc.id === rmkExistTemplateAccount?.id) return true;
    if (notifyAppNames.length === 0) return true;
    const accAppNames = (acc.applications ?? []).map((x) =>
      x.name.trim().toLowerCase(),
    );
    return notifyAppNames.every((n) => accAppNames.includes(n));
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState<TemplatifyAccountDetails | null>(
    null,
  );
  // Credentials proven against Templatify (account + api-key pair).
  const [credentialsOk, setCredentialsOk] = useState(false);
  const [templates, setTemplates] = useState<TemplatifyTemplateOption[]>([]);

  const requiredMsg = (fieldKey: string): string =>
    translate(`${i18n}.validation.required`, {
      field: translate(`${i18n}.fields.${fieldKey}`),
    });

  const pickNew = () => {
    setValue("accountMode", "new", { shouldDirty: true });
    setValue("autoCreateEnabled", true, { shouldDirty: true });
    setValidated(null);
  };
  const pickExisting = () => {
    setValue("accountMode", "existing", { shouldDirty: true });
    setValue("autoCreateEnabled", true, { shouldDirty: true });
  };

  const handleUpdateApiKeyToggle = (v: boolean) => {
    if (v) {
      setConfirmOpen(true);
    } else {
      setValue("isUpdateTemplateApiKey", false, { shouldDirty: true });
      setValue("rmkExistTemplateApiKey", "", { shouldDirty: true });
    }
  };

  const confirmApiKeyUpdate = () => {
    setValue("isUpdateTemplateApiKey", true, { shouldDirty: true });
    setValue("rmkExistTemplateApiKey", generateApiKey(), { shouldDirty: true });
    setConfirmOpen(false);
  };

  const cancelApiKeyUpdate = () => {
    setValue("isUpdateTemplateApiKey", false, { shouldDirty: true });
    setValue("rmkExistTemplateApiKey", "", { shouldDirty: true });
    setConfirmOpen(false);
  };

  const rmkExistTemplateApiKey = useWatch({
    control,
    name: "rmkExistTemplateApiKey",
  });

  const handleValidate = async (): Promise<void> => {
    if (!rmkExistTemplateAccount?.id || validating) return;
    setValidating(true);
    setCredentialsOk(false);
    setTemplates([]);
    try {
      // 1. Credentials check: authenticates against Templatify WITH the
      //    account's own api-key and lists its templates.
      const tpls = await validateTemplatifyCredentials(
        rmkExistTemplateAccount.name,
        rmkExistTemplateApiKey ?? "",
      );
      setTemplates(tpls);
      setCredentialsOk(true);

      // 2. Account card (admin route): group, applications, global access.
      const details = await getTemplatifyAccountDetails(
        rmkExistTemplateAccount.id,
      );
      setValidated(details);
      // Mirror the live Templatify config into the form so saving keeps it.
      setValue("autoCreateGlobal", details.isGlobalTemplateAccess, {
        shouldDirty: true,
      });
      setValue("templatifyGroupId", details.group?.id ?? null, {
        shouldDirty: true,
      });
      notify(`${i18n}.templatify.validate.success`, { type: "success" });
    } catch (err: unknown) {
      setValidated(null);
      notify(
        extractErrorMessage(err, `${i18n}.templatify.validate.failed`, translate),
        { type: "error" },
      );
    } finally {
      setValidating(false);
    }
  };

  const globalToggleBanner = (
    <Box sx={bannerSx}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
          {translate(`${i18n}.templatify.accessGlobal.title`)}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: rg.text3 }}>
          {translate(`${i18n}.templatify.accessGlobal.description`)}
        </Typography>
      </Box>
      <Controller
        name="autoCreateGlobal"
        control={control}
        render={({ field }) => (
          <Switch
            checked={Boolean(autoCreateGlobal)}
            onChange={(_, checked) => field.onChange(checked)}
          />
        )}
      />
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Mode cards */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={modeCardSx(accountMode === "new")}
            onClick={pickNew}
          >
            <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
              {translate(`${i18n}.templatify.createNew.title`)}
            </Typography>
            <Typography sx={{ fontSize: "11.5px", color: rg.text3 }}>
              {translate(`${i18n}.templatify.createNew.description`)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={modeCardSx(accountMode === "existing")}
            onClick={pickExisting}
          >
            <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
              {translate(`${i18n}.templatify.linkExisting.title`)}
            </Typography>
            <Typography sx={{ fontSize: "11.5px", color: rg.text3 }}>
              {translate(`${i18n}.templatify.linkExisting.description`)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Create new account ─────────────────────────────────────── */}
      {accountMode === "new" && (
        <>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={6}>
              <Controller
                name="rmkTemplateAccountName"
                control={control}
                rules={{ required: requiredMsg("rmkTemplateAccountName") }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label={translate(`${i18n}.fields.templatifyAccountName`, {
                      _: "Templatify account name",
                    })}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label={translate(`${i18n}.fields.templatifyGroup`, {
                  _: "Templatify group",
                })}
                InputLabelProps={{ shrink: true }}
                value={templatifyGroupId ?? ""}
                onChange={(e) =>
                  setValue("templatifyGroupId", e.target.value || null, {
                    shouldDirty: true,
                  })
                }
              >
                <MenuItem value="">—</MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <ApiKeyField
                name="rmkTemplateApiKey"
                label={translate(`${i18n}.fields.templatifyApiKey`, {
                  _: "Templatify api-key",
                })}
                required
                fullWidth
                copyNotifyKey={`${i18n}.notifications.rmkApiKeyCopied`}
                rules={{ required: requiredMsg("rmkTemplateApiKey") }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={templatifyApps}
                getOptionLabel={(a) => a.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={templatifyApps.filter((a) =>
                  templatifyExtraApplicationIds.includes(a.id),
                )}
                onChange={(_e, picked) =>
                  setValue(
                    "templatifyExtraApplicationIds",
                    picked.map((p) => p.id),
                    { shouldDirty: true },
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translate(`${i18n}.fields.templatifyApplications`, {
                      _: "Templatify application",
                    })}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
          {globalToggleBanner}
        </>
      )}

      {/* ── Link to existing account ───────────────────────────────── */}
      {accountMode === "existing" && (
        <>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={6}>
              <Controller
                name="rmkExistTemplateAccount"
                control={control}
                rules={{
                  validate: (v) =>
                    Boolean(v?.id) || requiredMsg("rmkTemplateAccountName"),
                }}
                render={({ fieldState }) => (
                  <Autocomplete
                    options={linkableAccounts}
                    getOptionLabel={(a) => a.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={
                      linkableAccounts.find(
                        (a) => a.id === rmkExistTemplateAccount?.id,
                      ) ?? null
                    }
                    onChange={(_e, picked) => {
                      setValue("rmkExistTemplateAccount", picked ?? null, {
                        shouldDirty: true,
                      });
                      setValue("rmkExistTemplateApiKey", picked?.apiKey ?? "", {
                        shouldDirty: true,
                      });
                      setValidated(null);
                      setCredentialsOk(false);
                      setTemplates([]);
                      // Silently mirror the account's live Templatify config
                      // so saving never flips settings the user didn't touch.
                      if (picked?.id) {
                        getTemplatifyAccountDetails(picked.id)
                          .then((d) => {
                            setValue(
                              "autoCreateGlobal",
                              d.isGlobalTemplateAccess,
                              { shouldDirty: false },
                            );
                            setValue(
                              "templatifyGroupId",
                              d.group?.id ?? null,
                              { shouldDirty: false },
                            );
                          })
                          .catch(() => undefined);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label={translate(
                          `${i18n}.fields.templatifyAccountName`,
                          { _: "Templatify account name" },
                        )}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? " "}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="rmkExistTemplateApiKey"
                control={control}
                rules={{
                  validate: (value, formValues) => {
                    if (!formValues.isUpdateTemplateApiKey) return true;
                    return (
                      (typeof value === "string" && value.length > 0) ||
                      requiredMsg("rmkTemplateApiKey")
                    );
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      setCredentialsOk(false);
                      setTemplates([]);
                    }}
                    fullWidth
                    required={Boolean(isUpdateTemplateApiKey)}
                    label={translate(`${i18n}.fields.templatifyApiKey`, {
                      _: "Templatify api-key",
                    })}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: credentialsOk ? (
                        <ValidateIcon
                          sx={{ fontSize: 18, color: rg.ok.line }}
                          titleAccess={translate(
                            `${i18n}.templatify.validate.credentialsOk`,
                            { _: "Connection verified" },
                          )}
                        />
                      ) : undefined,
                    }}
                    error={isUpdateTemplateApiKey && Boolean(fieldState.error)}
                    helperText={
                      isUpdateTemplateApiKey
                        ? (fieldState.error?.message ?? " ")
                        : " "
                    }
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Update-key guard */}
          <Box sx={bannerSx}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                {translate(`${i18n}.templatify.updateApiKey.title`)}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: rg.text3 }}>
                {translate(`${i18n}.templatify.updateApiKey.description`)}
              </Typography>
            </Box>
            <Switch
              checked={Boolean(isUpdateTemplateApiKey)}
              onChange={(_, checked) => handleUpdateApiKeyToggle(checked)}
            />
          </Box>

          {/* Validate */}
          <Box>
            <Button
              variant="contained"
              onClick={handleValidate}
              disabled={
                !rmkExistTemplateAccount?.id ||
                !(rmkExistTemplateApiKey ?? "").trim() ||
                validating
              }
              startIcon={
                validating ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <ValidateIcon sx={{ fontSize: 15 }} />
                )
              }
            >
              {translate(`${i18n}.templatify.validate.action`, {
                _: "Validate",
              })}
            </Button>
          </Box>

          {validated && (
            <Box
              sx={{
                border: `1px solid ${rg.border}`,
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: "9px 14px",
                  backgroundColor: rg.ok.bg,
                  color: rg.ok.fg,
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                {translate(`${i18n}.templatify.validate.verified`, {
                  _: "Account verified in Templatify",
                })}
              </Box>
              <Box sx={{ p: "4px 14px" }}>
                {[
                  {
                    label: translate(`${i18n}.fields.accountName`),
                    value: <>{validated.accountName}</>,
                  },
                  {
                    label: translate(`${i18n}.fields.templatifyGroup`, {
                      _: "Group",
                    }),
                    value: <>{validated.group?.name ?? "—"}</>,
                  },
                  {
                    label: translate(`${i18n}.fields.templatifyApplications`, {
                      _: "Application(s)",
                    }),
                    value: validated.applications.length ? (
                      <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                        {validated.applications.map((a) => (
                          <Chip key={a.id} size="small" label={a.name} />
                        ))}
                      </Box>
                    ) : (
                      <>—</>
                    ),
                  },
                  {
                    label: translate(`${i18n}.templatify.validate.templates`, {
                      _: "Templates",
                    }),
                    value: templates.length ? (
                      <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                        {templates.slice(0, 24).map((tp) => (
                          <Chip
                            key={tp.id}
                            size="small"
                            label={
                              tp.language
                                ? `${tp.templateName} · ${tp.language}`
                                : tp.templateName
                            }
                          />
                        ))}
                        {templates.length > 24 && (
                          <Chip
                            size="small"
                            label={`+${templates.length - 24}`}
                          />
                        )}
                      </Box>
                    ) : (
                      <>—</>
                    ),
                  },
                ].map((row) => (
                  <Box
                    key={row.label}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "170px 1fr",
                      gap: "10px",
                      alignItems: "center",
                      py: "7px",
                      borderBottom: `1px solid ${rg.border}`,
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <Typography component="span" sx={microSx}>
                      {row.label}
                    </Typography>
                    <Box component="span" sx={{ fontSize: "13px" }}>
                      {row.value}
                    </Box>
                  </Box>
                ))}
              </Box>
              {/* Informative only: mirrors the account's live Templatify
                  setting. Editable only when CREATING a new account. */}
              <Box
                sx={{
                  borderTop: `1px solid ${rg.border}`,
                  p: "12px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  backgroundColor: rg.bg2,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                    {translate(`${i18n}.templatify.accessGlobal.title`)}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: rg.text3 }}>
                    {translate(`${i18n}.templatify.accessGlobal.readonly`, {
                      _: "Current setting of the Templatify account",
                    })}
                  </Typography>
                </Box>
                <Switch checked={validated.isGlobalTemplateAccess} disabled />
              </Box>
            </Box>
          )}
        </>
      )}

      <UpdateTemplatifyKeyConfirmDialog
        open={confirmOpen}
        onConfirm={confirmApiKeyUpdate}
        onCancel={cancelApiKeyUpdate}
      />
    </Box>
  );
};

export default TemplatifyStep;
